import { useEffect, useEffectEvent, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { Navigate, useNavigate, useParams } from "react-router";
import type {
  AdmittedEntryResponse,
  AnalysisResponse,
  Citation,
  FileResponse,
  PositionResponse,
  PositionsResponse,
} from "@patent-studio/api-types";
import { refusalDetail, refusalWords } from "@/services/api-client";
import { useInventionElements } from "@/hooks/use-invention";
import { useNoveltyAnalysis, useStartRun } from "@/hooks/use-novelty-analysis";
import { useCommitNovelty, useNoveltyGate } from "@/hooks/use-novelty-gate";
import {
  useAcknowledgeGaps,
  useAddPosition,
  useAmendPosition,
  useConfirmPosition,
  useMarkElement,
  useOverrideVerdict,
  usePositions,
  useRemovePosition,
} from "@/hooks/use-positions";
import { useTask } from "@/hooks/use-tasks";
import { stageHeadingLabel, stagePath, tabPath } from "@/v2/stage-vocabulary";
import {
  DocumentDrawer,
  type DrawerDocument,
} from "../../documents/DocumentDrawer";
import { mappableElements } from "../positions/element-mapping";
import { NewPositionForm } from "../positions/NewPositionForm";
import { priorArtOf } from "../prior-art-list";
import { RemoveConfirmation } from "../positions/RemoveConfirmation";
import { Running } from "./RunProgress";
import { StepCard } from "@/components/patterns/StepCard";
import { AnalysisRow } from "./AnalysisRow";
import { AtAGlance } from "./AtAGlance";
import { RunBar } from "./RunBar";
import { ContinueFooter } from "./ContinueFooter";
import { SuggestedNovelties } from "./SuggestedNovelties";
import { StepBanner } from "../../notifications/StepChanges";
import { StagePage } from "../../layout/StagePage";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/ui/Panel";
import { LoadFailure } from "@/components/shared/LoadFailure";

/** The stage's side panel, in the design's words. */
function SidePanel() {
  return (
    <Panel
      as="aside"
      data-testid="novelty-side-panel"
      className="mt-6 grid gap-3 bg-muted/40 text-[12.5px] leading-relaxed text-muted-foreground sm:grid-cols-3"
    >
      <p>
        <strong className="text-foreground">Tested against the library.</strong>{" "}
        Every position is re-checked against all loaded prior art whenever
        references change.
      </p>
      <p>
        <strong className="text-foreground">Locked invention.</strong> Positions
        rest on locked elements. Anything a position reaches for outside the
        locked invention is flagged before it can stand.
      </p>
      <p>
        <strong className="text-foreground">Nothing here is filed.</strong>{" "}
        Locked positions are what claim strategy builds on.
      </p>
    </Panel>
  );
}

/**
 * A run that failed: the reason the server gave, and a way to run again. A
 * start the server refused is said too, so a refusal never reads as nothing
 * having happened.
 */
function Failed({
  analysis,
  onRunAgain,
  starting,
  refusal,
}: {
  analysis: AnalysisResponse;
  onRunAgain: () => void;
  starting: boolean;
  refusal: unknown;
}) {
  const refusedAgain = refusalWords(refusal);
  return (
    <Panel
      role="alert"
      tone="destructive"
      className="mt-6 flex flex-wrap items-center gap-3 text-[12.5px]"
    >
      <span className="flex-1">
        The analysis did not finish.
        {analysis.error !== null ? ` ${analysis.error}` : ""}
        {refusedAgain !== null && (
          <>
            {" "}
            <span data-testid="run-again-refused">
              It could not be started again: {refusedAgain}
            </span>
          </>
        )}
      </span>
      <Button
        variant="secondary"
        size="dense"
        type="button"
        onClick={onRunAgain}
        disabled={starting}
      >
        {starting ? "Starting…" : "Run again"}
      </Button>
    </Panel>
  );
}

/**
 * Opening a document at a cited passage, through the drawer the Invention page
 * uses. The citation is placed already (page, box), so the drawer marks it
 * without asking for passages; the run's finding reads as referenced. A
 * passage cited from what the disclosure admits opens on the disclosure it was
 * read from, since the admission is no file of its own.
 */
function usePassageViewer(
  files: readonly FileResponse[],
  admitted: readonly AdmittedEntryResponse[],
) {
  // The file by id and the citation, not a copy of the file: the drawer reads
  // the row as the task now serves it, so a stamp that lands while it is open
  // reaches the header.
  const [opened, setOpened] = useState<{
    fileId: string;
    citation: Citation;
  } | null>(null);
  const viewing = useMemo<DrawerDocument[] | null>(() => {
    const fileId =
      admitted.find((entry) => entry.entry_id === opened?.fileId)?.file_id ??
      opened?.fileId;
    const file = files.find((f) => f.file_id === fileId);
    if (opened === null || file === undefined) return null;
    const { citation } = opened;
    return [
      {
        file,
        marked: {
          tone: "referenced",
          passages: [
            { page: citation.page, box: citation.box, text: citation.quote },
          ],
        },
      },
    ];
  }, [files, admitted, opened]);
  return {
    viewing,
    open: (fileId: string, citation: Citation) => {
      setOpened({ fileId, citation });
    },
    close: () => setOpened(null),
  };
}

const NONE: ReadonlySet<string> = new Set();

/** The positions the screen asked to have assessed alone, and the run that carries them. */
interface Asked {
  /** The accepted run's id, or nothing while the start is still in flight. */
  runId: string | null;
  ids: ReadonlySet<string>;
  /** Asked while a run was in flight: the server takes one run at a time, so they wait for it to land. */
  queued: ReadonlySet<string>;
  /** The positions the server refused to assess, and its reason, until the next ask. */
  refused: { positionIds: ReadonlySet<string>; message: string } | null;
}

/** The server's reason for a refusal, or a plain sentence for a failure that has none. */
function reasonOf(failure: unknown): string {
  return refusalDetail(failure, "The request did not reach the server.");
}

/**
 * Assessing one position alone. The screen remembers which positions it has
 * asked about and which run carries them, since the served run carries
 * progress but not its scope. The server takes one run at a time, so a
 * position asked about while a run is in flight waits, and everything waiting
 * starts together in one run once it lands. A remembered row reads as assessing only while
 * that run is the one in flight, so a settled run, or a later full run started
 * elsewhere, leaves no row reading so on the strength of an old ask.
 */
function useAssessOne(taskId: string, analysis: AnalysisResponse) {
  const run = useStartRun(taskId);
  const [asked, setAsked] = useState<Asked>(() => ({
    runId: null,
    ids: NONE,
    queued: NONE,
    refused: null,
  }));
  const carried =
    analysis.status === "running" && analysis.run_id === asked.runId;
  const inFlight = run.isPending || carried;
  const start = (ids: readonly string[]) => {
    setAsked({ runId: null, ids: new Set(ids), queued: NONE, refused: null });
    run.mutate([...ids], {
      onSuccess: (accepted) =>
        setAsked((current) => ({ ...current, runId: accepted.run_id })),
      // A refusal is said on the rows it was for, and they stay as they were.
      onError: (failure) =>
        setAsked((current) => ({
          ...current,
          refused: { positionIds: new Set(ids), message: reasonOf(failure) },
        })),
    });
  };
  // What was asked while a run was in flight starts together once it lands.
  const startWaiting = useEffectEvent(() => {
    if (asked.queued.size > 0) start([...asked.queued]);
  });
  useEffect(() => {
    if (!inFlight) startWaiting();
  }, [inFlight]);
  return {
    assessing: inFlight ? new Set([...asked.ids, ...asked.queued]) : NONE,
    queued: asked.queued.size,
    refusalFor: (positionId: string) =>
      asked.refused?.positionIds.has(positionId) === true
        ? asked.refused.message
        : null,
    assess: (positionId: string) => {
      if (inFlight) {
        setAsked((current) => ({
          ...current,
          queued: new Set(current.queued).add(positionId),
          refused: null,
        }));
        return;
      }
      start([positionId]);
    },
  };
}

/** Drafting a position after the run, in the same form the first tab uses. */
function AddPosition({
  taskId,
  elements,
  busy,
}: {
  taskId: string;
  elements: ReturnType<typeof mappableElements>;
  busy: boolean;
}) {
  const add = useAddPosition(taskId);
  const [drafting, setDrafting] = useState(false);
  if (drafting) {
    return (
      <NewPositionForm
        elements={elements}
        busy={busy || add.isPending}
        onAdd={(body) => add.mutateAsync(body)}
        onDone={() => setDrafting(false)}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => setDrafting(true)}
      className="mt-2 flex w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-primary/10 bg-primary/[0.06] px-4 py-3 text-[13.5px] font-bold text-primary hover:bg-primary/[0.1]"
    >
      <Plus className="h-4 w-4" aria-hidden />
      Add new position
    </button>
  );
}

/** How many positions are waiting for the run in flight to land before they are assessed. */
function Queued({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <p role="status" className="mt-6 text-[12.5px] text-muted-foreground">
      {count === 1
        ? "1 more position will be assessed when this run lands."
        : `${count} more positions will be assessed when this run lands.`}
    </p>
  );
}

/** Every write a row can make on a position, and whether any is in flight. */
function usePositionWrites(taskId: string) {
  const amend = useAmendPosition(taskId);
  const confirm = useConfirmPosition(taskId);
  const acknowledgeGaps = useAcknowledgeGaps(taskId);
  const remove = useRemovePosition(taskId);
  const mark = useMarkElement(taskId);
  const rule = useOverrideVerdict(taskId);
  const busy =
    amend.isPending ||
    confirm.isPending ||
    acknowledgeGaps.isPending ||
    remove.isPending ||
    mark.isPending ||
    rule.isPending;
  return { amend, confirm, acknowledgeGaps, remove, mark, rule, busy };
}

/**
 * The positions as the run ruled them, one row each in list order. The
 * verdicts come from the analysis and the attorney's state from the positions,
 * joined by id.
 */
function Rows({
  taskId,
  analysis,
}: {
  taskId: string;
  analysis: AnalysisResponse;
}) {
  const { data: task } = useTask(taskId);
  const { data: positions } = usePositions(taskId);
  const { data: invention } = useInventionElements(taskId);
  const elements = mappableElements(invention);
  const byId = new Map(
    (positions?.positions ?? []).map((p) => [p.position_id, p] as const),
  );
  const viewer = usePassageViewer(task?.files ?? [], analysis.admitted);
  const { amend, confirm, acknowledgeGaps, remove, mark, rule, busy } =
    usePositionWrites(taskId);
  const one = useAssessOne(taskId, analysis);
  const [removing, setRemoving] = useState<PositionResponse | null>(null);
  return (
    <>
      <StepCard
        title="Novelty Analysis"
        intro="How each position stands against the prior art you loaded: confirm, amend or delete each position to proceed."
      >
        <Queued count={one.queued} />
        <ol data-testid="novelty-analysis-rows">
          {analysis.positions.map((position) => (
            <AnalysisRow
              key={position.position_id}
              position={position}
              attorney={byId.get(position.position_id)}
              elements={elements}
              busy={busy}
              actions={{
                onConfirm: (positionId) => confirm.mutate(positionId),
                onAmend: (positionId, body) =>
                  amend.mutateAsync({ positionId, body }),
                onRemove: setRemoving,
                onMark: (positionId, elementId, marked) =>
                  mark.mutateAsync({ positionId, elementId, mark: marked }),
                onOverride: (positionId, verdict, reason) =>
                  rule.mutateAsync({ positionId, verdict, reason }),
              }}
              onOpenPassage={viewer.open}
              onAssess={one.assess}
              onAcknowledgeGaps={(positionId, fileIds, reason) =>
                acknowledgeGaps.mutateAsync({ positionId, fileIds, reason })
              }
              acknowledgeRefusal={
                acknowledgeGaps.isError &&
                acknowledgeGaps.variables.positionId === position.position_id
                  ? reasonOf(acknowledgeGaps.error)
                  : null
              }
              assessing={one.assessing.has(position.position_id)}
              referencesRead={analysis.references_read}
              refusal={one.refusalFor(position.position_id)}
            />
          ))}
        </ol>
        <SuggestedNovelties taskId={taskId} />
        <AddPosition taskId={taskId} elements={elements} busy={busy} />
      </StepCard>
      <RemoveConfirmation
        position={removing}
        onCancel={() => setRemoving(null)}
        onConfirm={(position) => {
          setRemoving(null);
          remove.mutate(position.position_id);
        }}
      />
      <DocumentDrawer
        taskId={taskId}
        documents={viewer.viewing}
        onClose={viewer.close}
      />
    </>
  );
}

/**
 * The thesis stage, second tab: what the analysis found.
 *
 * Everything drawn here is served: the verdicts, the counts and the run's
 * state come from one read, and the run's motion is patched into that read
 * off the task's stream. A matter never run has nothing to show and is sent
 * back to the first tab, off the served state rather than the route table,
 * since the route table cannot know.
 */
export function NoveltyAnalysisPane() {
  const { taskId } = useParams<{ taskId: string }>();
  return taskId === undefined ? null : <AnalysisTab taskId={taskId} />;
}

/**
 * The gate out of the analysis: Continue to Claim Strategy, through the one
 * server act that checks every position is settled. A refusal keeps the
 * attorney here with the server's words.
 */
function ContinueGate({
  taskId,
  positions,
}: {
  taskId: string;
  positions: PositionsResponse | undefined;
}) {
  const navigate = useNavigate();
  const { data: task } = useTask(taskId);
  // Keyed on the prior art alone, as the other tabs key it, so every tab reads
  // one cached answer and a disclosure's status moving refetches nothing here.
  const gate = useNoveltyGate(
    taskId,
    priorArtOf(task?.files),
    positions?.positions ?? [],
  );
  const commit = useCommitNovelty(taskId);
  return (
    <ContinueFooter
      commit={gate.data?.commit}
      stale={gate.data?.analysis.stale ?? false}
      error={gate.isError ? gate.error : null}
      onRetry={() => void gate.refetch()}
      continuing={commit.isPending}
      refusal={commit.isError ? reasonOf(commit.error) : null}
      above={<StepBanner taskId={taskId} step="novelty_analysis" />}
      onContinue={() =>
        commit.mutate(undefined, {
          onSuccess: () => void navigate(stagePath(taskId, "claim-strategy")),
        })
      }
    />
  );
}

/**
 * The heading's claim about coverage, taken from what each verdict was served
 * as compared with: it says every position was assessed against every
 * readable document only when none is missing one and none is unassessed, and
 * nothing at all before the analysis has been read.
 */
export function ledeFor(analysis: AnalysisResponse | undefined): string {
  // Nothing read yet, or the read failed: say what the page is for, and claim
  // no coverage the server has not served.
  if (analysis === undefined) {
    return "Each position is checked against every readable prior art document. Work through the verdicts: confirm, amend or remove each position before continuing to Claim Strategy.";
  }
  const positions = analysis.positions;
  const unassessed = positions.filter(
    (p) => p.verdict === "not_assessed",
  ).length;
  const short = positions.filter(
    (p) => p.verdict !== "not_assessed" && p.not_assessed_against.length > 0,
  );
  if (unassessed === 0 && short.length === 0) {
    return "Every position has been checked against all loaded prior art. Work through the verdicts below: confirm, amend or remove each position before continuing to Claim Strategy.";
  }
  const said: string[] = [];
  if (short.length > 0) {
    const gaps = short.reduce(
      (sum, p) => sum + p.not_assessed_against.length,
      0,
    );
    const who =
      short.length === 1 ? "1 position was" : `${short.length} positions were`;
    const what = gaps === 1 ? "1 comparison" : `${gaps} comparisons`;
    said.push(
      `${who} not compared with every readable prior art document (${what} missing)`,
    );
  }
  if (unassessed > 0) {
    said.push(
      unassessed === 1
        ? "1 position has not been assessed"
        : `${unassessed} positions have not been assessed`,
    );
  }
  return `${said.join("; ")}. Assess them again, or confirm them without the missing documents, before continuing to Claim Strategy.`;
}

function AnalysisTab({ taskId }: { taskId: string }) {
  const {
    data: analysis,
    isError,
    error,
    refetch,
  } = useNoveltyAnalysis(taskId);
  const { data: positions } = usePositions(taskId);
  const run = useStartRun(taskId);

  if (analysis?.status === "not_started") {
    return (
      <Navigate
        to={tabPath(taskId, "thesis", "novelty", "prior-art")}
        replace
      />
    );
  }

  return (
    <StagePage
      measure="thesis"
      guide="novelty-analysis"
      testId="novelty-analysis-pane"
      taskId={taskId}
      eyebrow={`${stageHeadingLabel("thesis")} · Novelty Analysis`}
      title="Assess novelty of the invention"
      lede={ledeFor(analysis)}
    >
      <SidePanel />
      {isError && (
        // A failed read after a run lands leaves the bar full until it is
        // read again, so the failure carries the way to read it again.
        <LoadFailure
          error={error}
          fallback="The analysis could not be loaded."
          className="mt-6"
          onRetry={() => void refetch()}
        />
      )}
      {analysis !== undefined && analysis.status !== "failed" && (
        <RunBar
          analysis={analysis}
          attorney={positions?.positions ?? []}
          running={analysis.status === "running" || run.isPending}
          refusal={run.isError ? reasonOf(run.error) : null}
          onRerun={() => run.mutate(undefined)}
        />
      )}
      {analysis?.status === "running" && <Running analysis={analysis} />}
      {analysis?.status === "failed" && (
        <Failed
          analysis={analysis}
          starting={run.isPending}
          refusal={run.error}
          onRunAgain={() => run.mutate(undefined)}
        />
      )}
      {analysis !== undefined && analysis.status !== "running" && (
        <AtAGlance analysis={analysis} />
      )}
      {/* The rows stay while a run is in motion: the verdicts served are the
          previous ones until the run lands, and a row being assessed alone
          says so itself. */}
      {analysis !== undefined && <Rows taskId={taskId} analysis={analysis} />}
      {analysis !== undefined && (
        <ContinueGate taskId={taskId} positions={positions} />
      )}
    </StagePage>
  );
}
